// @flow
import React, { useMemo, useState } from 'react';
import styles from './styles.module.scss';
import Card from '../../components/Card';
import Toggler from '../../components/Toggler';
import Preloader from '../../components/Preloader';
import ResultTable from '../ResultTable/ResultTable';
import useWindowDimensions from '../../utils/windowDemensions';
import { getBreakpoints, MATRIX as matrixBreakpoints } from '../../utils/breakpoints';

type Props = {
  result: Array<{
    x: number,
    y: number,
    name: string,
    percents: number,
    bg_color: string,
  }> | undefined,
  isResultLoading: boolean,
  selectedTs: { value: string, label: string },
  setSelectedBB: { value: string, label: string },
  title: string,
  rate?: boolean,
  selectedBB: string,
  setVs3bet: (boolean) => void,
  isVs3bet: boolean,
  setVs4bet: (boolean) => void,
  isVs4bet: boolean,
  setIsEasy: (boolean) => void,
  isEasy: boolean,
  setSelectedTs: () => void,
  isPlo5: boolean,
  gameTable: string,
  setGameTable: () => void,
  ggps: string,
  setGgps: () => void,
  isFreeUser?: boolean,
  difficulty?: boolean,
  setDifficulty?: () => void,
}

const tsSelectorOptions = [
  { label: 'Low', value: 'low' },
  { label: 'Med', value: 'medium' },
  { label: 'Med+', value: 'high' },
  { label: 'High', value: 'high_plus' },
];

const tsSelectorOptionsMTT = [
  { label: 'MTT POT', value: 'mttpot' },
  { label: 'MTT 2.25', value: 'mtt2_25x' },
  { label: 'MTT ANTE POT', value: 'mttpot_ante' },
  { label: 'MTT ANTE 2.5', value: 'mttx25_ante' },
];

const tsSelectorOptionsHU = [
  { label: 'HU', value: 'hu' },
];

const tsSelectorOptions6MaxPlo5 = [
  { label: 'Low', value: 'low' },
  { label: 'High', value: 'high' },
  { label: 'ANTE POT', value: 'ante' },
  { label: 'ANTE 3.5', value: 'ante3_5x' },
  // { label: 'Straddle', value: 'straddle' },
  // { label: 'Straddle 3x', value: 'straddle3x' },
];

const tsSelectorOptionsHUPlo5 = [
  { label: 'Low', value: 'low' },
  { label: 'Med', value: 'medium' },
  { label: 'High', value: 'high' },
];

const tsSelectorOptionsGG = [
  { label: 'Low', value: 'low' },
  { label: 'High', value: 'high' },
];

const tsMain = [
  { label: '30', value: '30' },
  { label: '50', value: '50' },
  { label: '75', value: '75' },
  { label: '100', value: '100' },
  { label: '200', value: '200' },
];

const tsMainPlus = [
  { label: '30', value: '30' },
  { label: '50', value: '50' },
  { label: '75', value: '75' },
  { label: '100', value: '100' },
];

const tsGG = [
  { label: '10', value: '10' },
  { label: '30', value: '30' },
  { label: '50', value: '50' },
  { label: '100', value: '100' },
];

const tsMTTPOT = [
  { label: '10', value: '10' },
  { label: '20', value: '20' },
  { label: '30', value: '30' },
  { label: '40', value: '40' },
  { label: '50', value: '50' },
];

const tsMTT2 = [
  { label: '20', value: '20' },
  { label: '30', value: '30' },
  { label: '40', value: '40' },
  { label: '50', value: '50' },
];

const tsHU = [
  { label: '20', value: '20' },
  { label: '50', value: '50' },
  { label: '100', value: '100' },
  { label: '150', value: '150' },
  { label: '200', value: '200' },
];

const tsPlo5 = [
  { label: '20', value: '20' },
  { label: '50', value: '50' },
  { label: '75', value: '75' },
  { label: '100', value: '100' },
];

const difficultyOptions = [
  { label: 'Easy', value: 'easy' },
  { label: 'Default', value: 'default' },
  { label: 'Hard', value: 'hard' },
];

const MatrixResultTable = ({
  rate = false,
  result = [],
  isResultLoading,
  selectedTs,
  selectedBB,
  setVs3bet,
  isVs3bet,
  setSelectedTs,
  setSelectedBB,
  title,
  setVs4bet,
  isVs4bet,
  setIsEasy,
  isEasy,
  isPlo5,
  gameTable,
  setGameTable,
  ggps,
  setGgps,
  isFreeUser,
  difficulty,
  setDifficulty,
}: Props) => {
  const { width } = useWindowDimensions();
  const [percents, setPercents] = useState<any>('');

  const tsSelectorToggler = useMemo(() => {
    if (isPlo5) {
      setSelectedBB({ label: '100', value: '100' });
      return tsPlo5;
    }
    if (!isPlo5 && ggps === 'gg') {
      setSelectedBB({ label: '100', value: '100' });
      return tsGG;
    }
    switch (selectedTs?.value) {
      case 'mttpot': {
        setSelectedBB({ label: '50', value: '50' });
        return tsMTTPOT;
      }
      case 'mtt2_25x': {
        setSelectedBB({ label: '50', value: '50' });
        return tsMTT2;
      }
      case 'mttpot_ante': {
        setSelectedBB({ label: '50', value: '50' });
        return tsMTTPOT;
      }
      case 'mttx25_ante': {
        setSelectedBB({ label: '50', value: '50' });
        return tsMTTPOT;
      }
      case 'hu': {
        setSelectedBB({ label: '100', value: '100' });
        return tsHU;
      }
      case 'high_plus': {
        setSelectedBB({ label: '100', value: '100' });
        return tsMainPlus;
      }
      default: {
        setSelectedBB({ label: '100', value: '100' });
        return tsMain;
      }
    }
  }, [selectedTs, ggps]);

  // eslint-disable-next-line consistent-return
  const getTsSelectorOptions = () => {
    if (!isPlo5 && ggps === 'gg') return tsSelectorOptionsGG;
    if (!isPlo5 && ggps === 'mtt') return tsSelectorOptionsMTT;
    if (!isPlo5 && ggps === 'hu') return tsSelectorOptionsHU;
    if (!isPlo5) return tsSelectorOptions;
    if (isPlo5 && gameTable === '6max') return tsSelectorOptions6MaxPlo5;
    if (isPlo5 && gameTable === 'hu') return tsSelectorOptionsHUPlo5;
  };

  const HandlerPercents = ({ percentsOptions }: any) => {
    if (!percentsOptions) {
      return null;
    }

    return (
      <div className={styles.percentsWrapper}>
        <span className={styles.percentsTitle}>
          Counter:
          {' '}
          {percentsOptions.counter}
        </span>
        <span className={styles.percentsTitle}>
          Streak:
          {' '}
          {percentsOptions.streak}
        </span>
      </div>
    );
  };

  return (
    <Card className={styles.tables}>
      <div className={styles.tableControls}>
        <div className={styles.chooseStake}>
          {
          isPlo5
            && (
            <Toggler
              className={styles.selectTablePlo5}
              selected={gameTable}
              options={[{ label: '6MAX', value: '6max' }, { label: 'HU', value: 'hu' }]}
              onToggle={(e) => setGameTable(e.value)}
              use="red"
            />
            )
        }
          {
          !isPlo5
            && (
              <Toggler
                className={styles.selectTablePlo5}
                selected={ggps}
                options={[{ label: 'PS', value: 'ps' }, { label: 'GG', value: 'gg' }, { label: 'MTT', value: 'mtt' }, { label: 'HU', value: 'hu' }]}
                onToggle={(e) => (isFreeUser ? null : setGgps(e.value))}
                use="red"
                isFreeUser={isFreeUser}
              />
            )
        }

          <Toggler
            className={styles.selectTsWrapper}
            selected={difficulty}
            options={difficultyOptions}
            onToggle={(e) => (isFreeUser ? null : setDifficulty(e))}
            use="red"
            disabled={rate}
            isFreeUser={isFreeUser}
          />
        </div>
        <div className={styles.chooseStake}>
          <div className={styles.selectTsWrapperRakeStr}>
            <Toggler
              className={styles.fullWidth}
              selected={selectedTs}
              options={getTsSelectorOptions()}
              onToggle={(e) => (isFreeUser ? null : setSelectedTs(e))}
              use="red"
              disabled={rate}
              isFreeUser={isFreeUser}
            />
          </div>
          <Toggler
            className={styles.selectTsWrapper}
            selected={selectedBB}
            options={tsSelectorToggler}
            onToggle={(e) => (isFreeUser ? null : setSelectedBB(e))}
            use="red"
            selectedTSisAnte={['ante', 'ante3_5x', 'straddle', 'straddle3x'].includes(selectedTs.value)}
            selectedTsIsMedium={(selectedTs.value === 'medium' && isPlo5)}
            selectedGameTableIsHU={gameTable === 'hu'}
            isFreeUser={isFreeUser}
          />
        </div>
        <div className={styles.breadCrumbWrapper}>
          <h3>{title}</h3>
        </div>
        <div className={styles.percents}>
          <HandlerPercents percentsOptions={percents} />
        </div>
      </div>
      <Preloader isLoading={isResultLoading}>
        <div
          style={{
            height: `${getBreakpoints(matrixBreakpoints, 'height', width)
            * getBreakpoints(matrixBreakpoints, 'scale', width)}px`,
          }}
          className={styles.matrix}
        >
          <div className={styles.firstTable}>
            <Card
              use="transparent"
              scale={`scale(${getBreakpoints(matrixBreakpoints, 'scale', width)})`}
              className={styles.situationWrapper}
            >
              <div
                style={{ width: width < 640 ? width - 10 : 'auto' }}
                className={styles.situationTable}
              >
                <ResultTable
                  tableData={result}
                  setVs3bet={setVs3bet}
                  isVs3bet={isVs3bet}
                  setVs4bet={setVs4bet}
                  isVs4bet={isVs4bet}
                  setIsEasy={setIsEasy}
                  isEasy={isEasy}
                  setPercents={setPercents}
                  isFreeUser={isFreeUser}
                />
              </div>
            </Card>
          </div>
        </div>
      </Preloader>
    </Card>
  );
};
export default React.memo(MatrixResultTable);
